import { useEffect } from "react"
import { useLocation, useParams, useSearchParams, useNavigate, useOutletContext } from "react-router-dom"
import { useComputed, useSignal, useSignalEffect } from "@preact/signals-react"
import _ from "lodash-es"

import Button from 'react-bootstrap/Button'
import DropdownButton from 'react-bootstrap/DropdownButton'
import Dropdown from 'react-bootstrap/Dropdown'
import Form from 'react-bootstrap/Form'
import Nav from 'react-bootstrap/Nav'
import Pagination from 'react-bootstrap/Pagination'
import Spinner from 'react-bootstrap/Spinner'
import Table from 'react-bootstrap/Table'

import ConfirmationModal from "../../components/ConfirmationModal"
import CreateInstallationModal from "./components/CreateInstallationModal"
import DebugAccordion from "../../components/DebugAccordion"
import Icon from "../../components/Icon"
import InstallationsInfoModal from "./components/InstallationsInfoModal"
import PageTitle from "../../components/PageTitle.jsx"
import TimestampWithUTCTooltip from "../../components/TimestampWithUTCTooltip"


import { NotificationTypes, addNotification } from "../../stores/notification-store"
import { CreateEphemeralUser, CreateInstallation, DownloadInstallerPackage, DeactivateInstallation, fetchEphemeralRole } from "../../utils/api"
import { saveFile } from "../../utils/utils.js"

import useAgentManifest from "../../hooks/useAgentManifest.js"
import useInstallationsData from "../../hooks/useInstallationsData.js"
import useProfiles from "../../hooks/useProfiles.js"
// import useUserDataFromAccessToken from "../../hooks/useUserDataFromAccessToken.js"
import useUtgManifest from "../../hooks/useUtgManifest.js"

export default function InstallationList() {

    const navigate = useNavigate()
    const { beId, componentId } = useParams()
    const [searchParams] = useSearchParams()
    const debug = !!searchParams.get('debug')
    const { user, config, accessToken, userPermissionsSummary, accordionData: sidebarAccordionData } = useOutletContext()

    useEffect(() => {
        if (userPermissionsSummary.value && !userPermissionsSummary.value.canViewInstallations) {
            addNotification('You are not authorized to view installations for this account.', NotificationTypes.ERROR, true)
            navigate(`/`)
        }
    }, [userPermissionsSummary.value])

    const canEditSignal = useComputed(() => {
        return userPermissionsSummary?.value?.canEditInstallations
    })

    const { agents: availableAgentsSignal } = useAgentManifest(config, accessToken)
    const { utgs: availableUtgsSignal } = useUtgManifest(config, accessToken)
    const { installations: installationsSignal, loading: installationsLoadingSignal, error: installationsErrorSignal, modified: installationsModifiedSignal, fetchData: fetchInstallationsData } = useInstallationsData(beId, config, accessToken)
    const { profiles: availableProfilesSignal, loading: profilesLoadingSignal, error: profilesErrorSignal } = useProfiles(beId, config, accessToken)

    const currentPage = useSignal(1)
    const rowsPerPageSignal = useSignal(parseInt(window.localStorage.getItem('pagesize')) || 250)
    const filterString = useSignal("")
    const sortColumn = useSignal('')

    const SortDirectionType = {
        ASCENDING: 1,
        DESCENDING: 2
    }
    const sortDirection = useSignal(SortDirectionType.DESCENDING)

    useSignalEffect(() => {
        window.localStorage.setItem('pagesize', rowsPerPageSignal.value)
    })

    const showDownloadProgress = useSignal(false)
    const downloadProgressMessage = useSignal('')

    const showDeactivateModal = useSignal(false)
    const installToDeactivate = useSignal()
    const installToDeactivateNickname = useSignal('')

    const location = useLocation()
    let intent = location && location.state ? location.state.intent : null
    const intendToShowNewInstallationModalSignal = useSignal(intent == "install" || intent == "connect")
    intent = null
    const showInstallationsInfoModalSignal = useSignal(false)

    const showNewInstallationModalComputed = useComputed(() => {
        // this will wait for all data to be loaded on the page first 
        return intendToShowNewInstallationModalSignal.value &&
            !!availableUtgsSignal.value.length &&
            !!availableAgentsSignal.value.length &&
            !!gaAgent.value.length &&
            !!gaUtg.value.length
    })

    const sortedProfilesSignal = useComputed(() => {
        if (availableProfilesSignal.value.length == 0) {
            return []
        }
        const sorted = availableProfilesSignal.value.sort((profile1, profile2) => {
            if (profile1.name.toLowerCase() < profile2.name.toLowerCase()) {
                return -1
            }
            else if (profile1.name == profile2.name) {
                return 0
            }
            return 1
        })
        return sorted
    })

    const filteredData = useComputed(() => {
        if (!filterString.value) {
            return installationsSignal.value ? [...installationsSignal.value] : []
        }

        // Convert the filter to lowercase
        const lowerCaseFilter = filterString.value.toLowerCase()

        // Filter the data based on the filter text
        return installationsSignal.value.filter(item => {

            // Limit search to these fields
            let searchData = {
                nickname: item.manifest.machine.nickname,
                verificationId: item.verificationId,
                profileId: item.profileId ? item.profileId.toString() : '',
                utgInstallLocation: item.manifest.components[0].installLocation,
                agentInstallLocation: item.manifest.agent.installLocation
            }

            return Object.values(searchData).some(value => {
                if (value && (typeof (value) == 'string')) {
                    return value.toString().toLowerCase().includes(lowerCaseFilter)
                }
            })
        })
    })

    const sortedData = useComputed(() => {

        // just return filtered data if no sorting
        if (!sortColumn.value.length) {
            return [...filteredData.value]
        }

        let _sortedData = [...filteredData.value]
        return _sortedData.sort((a, b) => {
            let _a = _.get(a, sortColumn.value, null)
            let _b = _.get(b, sortColumn.value, null)

            // Convert nulls to empty strings for sorting purposes
            _a = _a ? _a : ''
            _b = _b ? _b : ''

            // convert values to lowercase
            if (_a && _a.length) {
                _a = _a.toLowerCase()
            }
            if (_b && _b.length) {
                _b = _b.toLowerCase()
            }

            if (sortDirection.value == SortDirectionType.DESCENDING) {
                if (_a < _b) return -1
                if (_a > _b) return 1
                return 0
            }
            else {
                if (_a > _b) return -1
                if (_a < _b) return 1
                return 0
            }
        })
    })

    const totalPages = useComputed(() => {
        return Math.ceil(filteredData.value.length / parseInt(rowsPerPageSignal.value, 10))
    })

    const displayData = useComputed(() => {
        // Calculate the starting and ending indexes for pagination
        const startIndex = (currentPage.value - 1) * rowsPerPageSignal.value
        const endIndex = startIndex + rowsPerPageSignal.value

        return sortedData.value.slice(startIndex, endIndex)
    })
    const startDisplayRowNumber = useComputed(() => {
        return ((currentPage.value - 1) * rowsPerPageSignal.value) + 1
    })
    const endDisplayRowNumber = useComputed(() => {
        const startIndex = (currentPage.value - 1) * rowsPerPageSignal.value
        const endIndex = startIndex + rowsPerPageSignal.value
        return endIndex < filteredData.value.length ? endIndex : filteredData.value.length
    })

    const accordionData = [
        { name: 'Installations', data: installationsSignal },
        { name: 'Display Data', data: displayData },
        { name: 'Sorted Data', data: sortedData },
        { name: 'Filtered Data', data: filteredData },
        { name: 'Access Token', data: accessToken },
        { name: 'App Config', data: config },
        { name: 'AvailableUtgs', data: availableUtgsSignal.value },
        { name: 'AvailableAgents', data: availableAgentsSignal.value }
    ]

    const handlePageSizeSelect = (v) => {
        currentPage.value = 1
        rowsPerPageSignal.value = parseInt(v, 10)
    }
    const handleFilterStringChange = (e) => {
        currentPage.value = 1
        filterString.value = e.currentTarget.value
    }
    const handleCopyClipboard = item => {
        if (item) {
            // place the data on the users clipboard if the browser supports it
            if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
                navigator.clipboard.writeText(JSON.stringify(item, null, 2));
                addNotification(`The data for ${item.manifest.machine.nickname} has been copied to your clipboard.`, NotificationTypes.SUCCESS, false, 'Copy Succeeded')
            }
            else {
                addNotification('This browser does not support copying data to the clipboard.', NotificationTypes.INFO, false, 'Copy Failed')
            }
        }
    }
    const handleDeactivate = item => {
        try {
            if (item) {
                installToDeactivate.value = item
                showDeactivateModal.value = true
                installToDeactivateNickname.value = installToDeactivate.value.manifest.machine.nickname
                console.log('handleDeactivate', installToDeactivate.value)
            }
        }
        catch (error) {
            //
        }
    }

    const deactivateInstall = async () => {
        showDeactivateModal.value = false
        try {
            /*const deactivateInstallResult =*/ await DeactivateInstallation(config, installToDeactivate.value.businessEntityId, installToDeactivate.value.id, accessToken)
            installationsModifiedSignal.value = true; // flag useInstallationsData to reload data when called again
            addNotification('Install successfully deactivated', NotificationTypes.SUCCESS, true)
            await fetchInstallationsData(beId, config, accessToken)
        }
        catch (error) {
            console.log(error)
            addNotification(`Unable to deactivate installation. ${error}`, NotificationTypes.ERROR, true)
        }
    }

    // Show notification when user clicks on download link
    const handleDownloadInstaller = () => {
        showDownloadProgress.value = true
        downloadProgressMessage.value = 'Requesting installer file'
        setTimeout(() => {
            showDownloadProgress.value = false
            addNotification('Downloading installer file. Your download should begin shortly.', NotificationTypes.SUCCESS, true)
        }, 2000);
    }

    const installerDownloadUrl = useComputed(() => {
        return config.installerDownloadUrl
    })

    const handleDownloadPackage = async (item) => {
        // set showDownloadProgress to true to show spinner
        showDownloadProgress.value = true
        downloadProgressMessage.value = 'Preparing to download installer package'
        try {
            if (item) {
                const installerPackageResponse = await DownloadInstallerPackage(config, item.verificationId)
                saveFile(installerPackageResponse, "commandcenter.zip")
                addNotification('Downloading installer package. Your download should begin shortly.', NotificationTypes.SUCCESS, true)
                showDownloadProgress.value = false
            }
        }
        catch (error) {
            console.log(error)
            addNotification(`Unable to download installer package. ${error}`, NotificationTypes.ERROR, true)
            showDownloadProgress.value = false
        }
    }

    const handleHeaderTableClick = datafield => {
        // if already sorted on this column then toggle sort direction
        if (datafield == sortColumn.value) {
            sortDirection.value = sortDirection.value == SortDirectionType.DESCENDING ? SortDirectionType.ASCENDING : SortDirectionType.DESCENDING
            return
        }

        // update sort column - the computed functions will then run and render the sorted data
        sortColumn.value = datafield
    }

    // Create Installation Logic
    const installationManifestObjectSignal = useSignal({})
    const gaUtg = useComputed(() => {
        if (availableUtgsSignal.value.length) {
            const filteredUtgs = availableUtgsSignal.value.filter((a) => a.generalAvailability === true
            )
            return filteredUtgs[0].version
        }
    })

    const gaAgent = useComputed(() => {
        if (availableAgentsSignal.value.length) {
            const filteredAgents = availableAgentsSignal.value.filter((a) => a.generalAvailability === true
            )
            return filteredAgents[0].version
        }
    })
    const handleShowInstallationModal = () => {
        intendToShowNewInstallationModalSignal.value = true
    }

    const handleCreateInstallation = async () => {
        intendToShowNewInstallationModalSignal.value = false
        showDownloadProgress.value = true
        downloadProgressMessage.value = 'Creating Installation'

        // Access user data retrieved using AccessToken

        if (!user) {
            window.alert("User is undefined")
            return
        }

        const currentUserId = user.id
        const currentDate = new Date()
        const lasUpdatedAt = currentDate.toISOString()
        const startDate = currentDate.toLocaleDateString("en-US", {
            month: "2-digit",
            day: "2-digit",
            year: "numeric",
        })
        currentDate.setFullYear(currentDate.getFullYear() + 99)
        const endDate = currentDate.toLocaleDateString("en-US", {
            month: "2-digit",
            day: "2-digit",
            year: "numeric",
        })
        try {
            // Fetch the ephemeral role id
            const ephemeralRoleResponse = await fetchEphemeralRole(beId, config, accessToken)
            const ephemeralRoleId = ephemeralRoleResponse.objectRoleMapping[0].roleId
            const ephemeralUserRequestBody = {
                "authenticationCode": null,
                "applicationId": 4,
                "createdBy": `${currentUserId}`,
                "allowDuplicateSessions": true,
                "isRevoked": false,
                "lastUpdatedAt": `${lasUpdatedAt}`,
                "lastUpdatedBy": 1,
                "businessEntityId": `${beId}`,
                "lifespan": {
                    "lifeSpanType": 20,
                    "startDate": `${startDate}`,
                    "endDate": `${endDate}`
                },
                "roleMappings": [
                    {
                        "lastUpdatedAt": `${lasUpdatedAt}`,
                        "lastUpdatedBy": 1,
                        "roleId": `${ephemeralRoleId}`,
                        "businessEntityId": `${beId}`,
                        "ephemeralUserId": null
                    }
                ],
                "userSessions": []
            }

            // Create the ephermal user and set the returned values to use for create installation request
            const createEphemeralUserResponse = await CreateEphemeralUser(beId, config, ephemeralUserRequestBody, accessToken)
            const ephemeralUserAuthenticationCode = createEphemeralUserResponse.ephemeralUser.authenticationCode
            const ephemeralUsersessionToken = createEphemeralUserResponse.ephemeralUser.userSessions[0].sessionToken
            installationManifestObjectSignal.value.manifest.credentials.authenticationCode = ephemeralUserAuthenticationCode
            installationManifestObjectSignal.value.manifest.credentials.sessionToken = ephemeralUsersessionToken

            // Create the install and download the installer exe
            const createInstallationResponse = await CreateInstallation(beId, config, installationManifestObjectSignal.value, accessToken)
            if (createInstallationResponse) {
                showDownloadProgress.value = false
                const formattedVerificationId = formatVerificationId(createInstallationResponse)
                const installationName = createInstallationResponse.installation.manifest.machine.nickname
                const notificationMessage = <span>The installation package has been created. Your Verification ID is {formattedVerificationId}. Write down the verification ID as it will be required during the installation process. The package can be downloaded <a href={installerDownloadUrl}>here</a>.</span>
                addNotification(notificationMessage, NotificationTypes.SUCCESS, true, `Installation ${installationName} created. Verification ID: ${formattedVerificationId}`)
                await fetchInstallationsData(beId, config, accessToken)
            }
            else {
                throw "Failed to create installation. Server response did not contain a valid Verification Id"
            }
        }
        catch (error) {
            addNotification(`Error creating installation: ${error.message}`, NotificationTypes.ERROR)
        }
    }

    function formatVerificationId(createInstallationResponse) {
        // Check if the object structure exists and contains an 8-character string
        if (
            typeof createInstallationResponse.installation.verificationId === 'string' &&
            createInstallationResponse.installation.verificationId.length === 8
        ) {
            // Insert a space in the middle (4th and 5th character)
            const verificationId = createInstallationResponse.installation.verificationId;
            return verificationId.slice(0, 4) + " " + verificationId.slice(4);
        }

        // Return null or an error message if the input is invalid
        return "Error formatting installation verification id";
    }

    function handleCancelModalInstallationsInfo() {
        showInstallationsInfoModalSignal.value = false
    }

    function TableRows({ items }) {
        if (items && items.length) {
            const rows = items.map(item => {
                return <TableRow item={item} key={`${item.id}`} />
            })

            return (
                <tbody>{rows}</tbody>
            )
        }
        else if (items) {
            return (
                <tbody><tr><td colSpan="9" className="text-center">No installations found</td></tr></tbody>
            )
        }
    }

    function TableRow({ item }) {
        return (
            <tr>
                <td>
                    <Dropdown>
                        <Dropdown.Toggle variant="link" size="sm" id="actions">
                            <Icon icon="gear" width="16" height="16"></Icon>
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                            <Dropdown.Item onClick={() => handleCopyClipboard(item)}>Copy Row as JSON</Dropdown.Item>
                            {canEditSignal.value && <Dropdown.Item onClick={() => handleDeactivate(item)}>Deactivate Install</Dropdown.Item>}
                            <Dropdown.Item as="a" href={installerDownloadUrl} download onClick={() => handleDownloadInstaller()}>Download Installer</Dropdown.Item>
                            <Dropdown.Item onClick={() => handleDownloadPackage(item)}>Download Install Package</Dropdown.Item>
                        </Dropdown.Menu>
                    </Dropdown>
                </td>
                <td>{item.manifest.machine.nickname}</td>
                <td>{item.type == 10 ? 'New UTG' : 'Existing UTG'}</td>
                <td>{item.verificationId}</td>
                <td>{item.businessEntityId}</td>
                <td><TimestampWithUTCTooltip date={item.lastUpdatedAt}></TimestampWithUTCTooltip></td>
                <td>{item.profileId}</td>
                <td>{item.manifest.components[0].installLocation}</td>
                <td>{item.manifest.agent.installLocation}</td>
            </tr>
        )
    }

    function PaginationItems() {
        let items = []
        for (let i = 1; i <= totalPages.value; i++) {
            items.push(
                <Pagination.Item key={i} active={i === currentPage.value} onClick={() => currentPage.value = i}>{i}</Pagination.Item>
            )
        }
        return items
    }

    function TableColumnHeader({ title, sortable, datafield }) {
        // non-sortable columns do not have click handlers
        if (!sortable) {
            return (
                <th>{title}</th>
            )
        }

        // sortable column but isn't currently sorted on this column. Add click handler but no caret indicators
        if (datafield != sortColumn.value) {
            return (
                <th className="sortable-table-header" onClick={() => handleHeaderTableClick(datafield)}>{title}</th>
            )
        }

        // datafield matches sortColumn signal so we need to add a caret icon. sortDirection signal determines the direction
        if (sortDirection.value == SortDirectionType.DESCENDING) {
            return (
                <th className="sortable-table-header" onClick={() => handleHeaderTableClick(datafield)}>{title}
                    <Icon icon="caret-down" width="16" height="16"></Icon>
                </th>
            )
        }

        // else must be ascending
        return (
            <th className="sortable-table-header" onClick={() => handleHeaderTableClick(datafield)}>{title}
                <Icon icon="caret-up" width="16" height="16"></Icon>
            </th>
        )
    }

    function DownloadProgress() {
        if (showDownloadProgress.value) {
            return (
                <div className="d-flex flex-column align-items-center justify-content-center">
                    <Spinner className="mt-5" animation="border" role="status">
                        <span className="visually-hidden">{downloadProgressMessage}</span>
                    </Spinner>
                    <p className="mt-3">{downloadProgressMessage}</p>
                </div>
            )
        }
    }

    if (installationsLoadingSignal.value || !installationsSignal.value) {
        return (
            <div className="d-flex justify-content-center">
                <Spinner className="mt-5 align-center" animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                </Spinner>
            </div>
        )
    }

    return (
        <>
            <DownloadProgress></DownloadProgress>
            <ConfirmationModal show={showDeactivateModal.value} handleCancel={() => showDeactivateModal.value = false} handleConfirm={() => { deactivateInstall() }} message={`Are you sure you want to deactivate install: ${installToDeactivateNickname.value}? This action cannot be undone.`} title={"Deactivating Install"} />
            {showNewInstallationModalComputed.value && (
                <CreateInstallationModal
                    show={showNewInstallationModalComputed.value}
                    handleCancel={() => intendToShowNewInstallationModalSignal.value = false}
                    handleConfirm={() => handleCreateInstallation()}
                    businessEntityId={beId}
                    availableAgentsVersions={availableAgentsSignal}
                    availableProfiles={sortedProfilesSignal.value}
                    availableUtgVersions={availableUtgsSignal}
                    installationManifestObject={installationManifestObjectSignal}
                    defaultUtgVersion={gaUtg.value}
                    defaultAgentVersion={gaAgent.value}
                    intent={intent}
                />
            )}
            <PageTitle title='Installation List' canEditSignal={canEditSignal} />
            <Nav className="justify-content-between px-3 py-1 my-2 bg-light">
                <DropdownButton variant="outline-secondary" className="me-auto" title={`${rowsPerPageSignal} records per page`} id="pageSize-dropdown" onSelect={handlePageSizeSelect}>
                    <Dropdown.Item eventKey="10">10</Dropdown.Item>
                    <Dropdown.Item eventKey="25">25</Dropdown.Item>
                    <Dropdown.Item eventKey="50">50</Dropdown.Item>
                    <Dropdown.Item eventKey="250">250</Dropdown.Item>
                    <Dropdown.Item eventKey="500">500</Dropdown.Item>
                    <Dropdown.Item eventKey="1000">1000</Dropdown.Item>
                </DropdownButton>
                <Nav.Item className="mt-1 me-2" maxWidth={"calc(100% - 407.25px)"}>
                    <Form.Control type="text" placeholder="Search" size="sm" htmlSize="75" onChange={handleFilterStringChange} />
                </Nav.Item>
                <Nav.Item>
                    <Button variant="outline-secondary" onClick={() => { showInstallationsInfoModalSignal.value = true }}><Icon icon="info-circle" height={16} width={16}></Icon></Button>
                </Nav.Item>
                {canEditSignal.value && <Nav.Item className="mt-1">
                    <Button size="sm" variant="outline-secondary" className="d-flex align-items-center justify-content-center" onClick={handleShowInstallationModal}><Icon icon='plus-circle' width="16" height="16" ></Icon>&nbsp;New</Button>
                </Nav.Item>}
            </Nav>
            <Table responsive striped hover size="sm" className="cc-table">
                <thead>
                    <tr>
                        <TableColumnHeader title={'Action'} sortable={false}></TableColumnHeader>
                        <TableColumnHeader title={'Installation Name'} sortable={true} datafield={'manifest.machine.nickname'}></TableColumnHeader>
                        <TableColumnHeader title={'Installation Type'} sortable={true} datafield={'type'}></TableColumnHeader>
                        <TableColumnHeader title={'Verification ID'} sortable={true} datafield={'verificationId'}></TableColumnHeader>
                        <TableColumnHeader title={'Parent Account'} sortable={true} datafield={'businessEntityId'}></TableColumnHeader>
                        <TableColumnHeader title={'Creation Date'} sortable={true} datafield={'lastUpdatedAt'}></TableColumnHeader>
                        <TableColumnHeader title={'Profile'} sortable={true} datafield={'profileId'}></TableColumnHeader>
                        <TableColumnHeader title={'UTG Install Location'} sortable={true} datafield={'manifest.components[0].installLocation'}></TableColumnHeader>
                        <TableColumnHeader title={'Agent Install Location'} sortable={true} datafield={'manifest.agent.installLocation'}></TableColumnHeader>
                    </tr>
                </thead>
                <TableRows items={displayData.value} />
                <tfoot><tr><td className="fs-6" colSpan={19}>Displaying {startDisplayRowNumber.value}-{endDisplayRowNumber.value} of {filteredData.value.length}</td></tr></tfoot>
            </Table>
            <Pagination className="px-3 d-flex flex-wrap" size="sm"><PaginationItems /></Pagination>
            <DebugAccordion data={accordionData} debug={debug} />

            <InstallationsInfoModal
                showSignal={showInstallationsInfoModalSignal}
                handleCancel={handleCancelModalInstallationsInfo}
                referenceGuid={config && config.value && config.value.referenceGuid ? config.value.referenceGuid : null}
            ></InstallationsInfoModal>
        </>
    )
}